
import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font, PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';

Font.register({
  family: 'Roboto',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/roboto/v27/KFOmCnqEu92Fr1Mu4mxP.ttf' },
    { src: 'https://fonts.gstatic.com/s/roboto/v27/KFOlCnqEu92Fr1MmEU9fBBc9.ttf', fontWeight: 700 },
  ],
});

interface Payment {
  amount: number;
  term: string;
  academic_year: string;
  admission_no: string;
  school_id: string;
  item_code: string;
  description: string;
  status: string;
  ref_no: string;
  is_optional: string;
  student_type: string;
  payment_date: string;
  discount: number;
  fines: number;
  checked: boolean;
  amount_paid: number;
}

interface SchoolData {
  school_name: string;
  badge_url: string;
  academicYear: string;
  address: string;
  email_address: string;
  primary_contact_number: string;
}

interface FeesInvoicePDFProps {
  data: Payment[];
  school_data: SchoolData;
  student_name: string;
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    padding: 20,
    fontSize: 10,
    fontFamily: 'Roboto',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  logo: {
    width: 50,
    height: 50,
    marginRight: 15,
  },
  schoolInfo: {
    flex: 1,
  },
  schoolName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  schoolSub: {
    fontSize: 10,
    color: '#666',
    marginBottom: 2,
  },
  receiptTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2d3748',
    textAlign: 'center',
    marginVertical: 15,
    borderBottomWidth: 2,
    borderBottomColor: '#4CAF50',
    paddingBottom: 8,
  },
  section: {
    marginBottom: 12,
  },
  studentInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  infoBlock: {
    width: '48%',
  },
  label: {
    fontSize: 9,
    color: '#718096',
    marginBottom: 2,
  },
  value: {
    fontSize: 10,
    color: '#2d3748',
    fontWeight: 'bold',
    marginBottom: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f7fafc',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  col1: { width: '8%' },
  col2: { width: '62%' },
  col3: { width: '15%' },
  col4: { width: '15%' },
  summaryContainer: {
    marginTop: 15,
    padding: 12,
    backgroundColor: '#f8fafc',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  totalRow: {
    borderTopWidth: 1,
    borderTopColor: '#cbd5e0',
    paddingTop: 8,
    marginTop: 8,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#718096',
    fontSize: 9,
  },
});


const FeesInvoicePDF: React.FC<FeesInvoicePDFProps> = ({ data=[], school_data, student_name }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Image
          style={styles.logo}
          src={school_data.badge_url || 'https://via.placeholder.com/50'}
        />
        <View style={styles.schoolInfo}>
          <Text style={styles.schoolName}>{school_data.school_name}</Text>
          <Text style={styles.schoolSub}>{school_data.address}</Text>
          {/* <Text style={styles.schoolSub}>
            {school_data.primary_contact_number} • {school_data.email_address}
          </Text> */}
          <Text style={styles.schoolSub}>Academic Year: {school_data.academicYear}</Text>
        </View>
      </View>

      <Text style={styles.receiptTitle}>PAYMENT RECEIPT</Text>

      {/* Student Information */}
      <View style={styles.studentInfo}>
        <View style={styles.infoBlock}>
          <Text style={styles.label}>STUDENT NAME</Text>
          <Text style={styles.value}>{student_name}</Text>
          
          <Text style={styles.label}>ADMISSION NUMBER</Text>
          <Text style={styles.value}>{data[0]?.admission_no}</Text>
        </View>
        
        <View style={styles.infoBlock}>
          <Text style={styles.label}>RECEIPT NUMBER</Text>
          <Text style={styles.value}>{data[0]?.ref_no || ''}</Text>
          
          <Text style={styles.label}>PAYMENT DATE</Text>
          <Text style={styles.value}>{data[0]?.payment_date}</Text>
        </View>
      </View>

      {/* Items Table */}
      <View style={styles.section}>
        <View style={styles.tableHeader}>
          <Text style={styles.col1}>#</Text>
          <Text style={styles.col2}>DESCRIPTION</Text>
          <Text style={styles.col3}>AMOUNT</Text>
        </View>

        {data.map((payment, i) => (
          <View style={styles.tableRow} key={i}>
            <Text style={styles.col1}>{i + 1}</Text>
            <Text style={styles.col2}>{payment.description}</Text>
            <Text style={styles.col3}>{payment.amount}</Text>
          </View>
        ))}
      </View>

      {/* Payment Summary */}
      <View style={styles.summaryContainer}>
        <View style={styles.summaryRow}>
          <Text>Subtotal:</Text>
          <Text>{data.reduce((acc, payment) => Math.floor(acc) +  Math.floor(payment.amount), 0)}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text>Discounts:</Text>
          <Text>-{data.reduce((acc, payment) => Math.floor(acc) +  Math.floor(payment.discount), 0)}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text>Fines:</Text>
          <Text>+{data.reduce((acc, payment) => Math.floor(acc) +  Math.floor(payment.fines), 0)}</Text>
        </View>
        <View style={[styles.summaryRow, styles.totalRow]}>
          <Text style={{ fontWeight: 'bold' }}>TOTAL PAID:</Text>
          <Text style={{ fontWeight: 'bold' }}>
          {data.reduce((acc, payment) => Math.floor(acc) +  Math.floor(payment.amount_paid), 0)}
          </Text>
        </View>
      </View>

      <Text style={styles.footer}>
        {school_data.school_name} • Powered by Elite Ed Tech 
      </Text>
    </Page>
  </Document>
);


export default FeesInvoicePDF;
